import {
  listPours, signPour as signPortPour, listPourAdministrations, recordPourAdministration,
  completeMedication as completePortMedication, listPatientHolds, listStatusEvents,
} from '@/api2/medicationPours';
import {
  holdMedication as holdPortMedication, clearMedicationHold, listScheduledHolds, cancelScheduledHold,
  replaceAdministrationSchedule, replaceMedicationOrderSchedule,
} from '@/api2/medicationAdministration';
import { ApiError } from '@/api2/client';
import {
  poursFromServer, templateFromPours, administrationsFromServer, poursFromPort, administrationsFromPort, pourRequestForPort,
} from '@/utils/prepourPours';
import {
  holdBodyForPort, changeBodyForPort, moveBodyForPort, holdsFromPort, eventsFromPort, orderIdOf, hasNoOrder, UnreadableTimeError,
} from '@/utils/prepourStatusRequests';

/**
 * Pre-pour records, one function per kind, keyed by patient (`<account>-<patient>`, or 'sample').
 *
 * Every layout of the pre-pour page (A–E) reaches these records only through here, so moving a kind
 * from the browser to the server changes this module and the handlers that await it — never a layout.
 * A real patient's pours, administrations, Completed, holds, changes and History are the port's
 * (decision D2, main #327–#332 and #336), through the native client @/api2/medicationPours and nursing's
 * own routes (@/api2/medicationAdministration); the sample sheet (no patient) and the kinds main still
 * keeps in the browser — discontinues, the change log of a discontinue — stay in this browser's
 * localStorage under `ppProto:<kind>:<patient>`, purged at sign-out (auth/oidcClient.js). The draft and the
 * auto-fill switch are not here: they stay in the browser, as main has them.
 *
 * `ctx` is { account, patient, actorId } as the page builds it. Only `patient` — the chart's id — is
 * used: the actor is the token, and no body names an account or an actor (main's `actorid` was the
 * actor whenever KEYON_AUTH_MODE was not `enforce`).
 */

/** A real patient's records live on the server; the sample sheet's never leave the browser. */
export const isRemote = (pk) => !!pk && pk !== 'sample';

/**
 * A refusal, in the words the page reads: `code` is main's where the page branches on it
 * (needs_discontinue) and the server's otherwise; `message` is the server's sentence, which says what
 * to do. `details` carries what the server said about it (a shortfall per medication, the window).
 */
export class PrePourError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.code = code;
    Object.assign(this, extra);
  }
}

/** The port's refusal codes as main's, where main's page or its words know them. */
const MAIN_CODES = {
  prepour_short_supply: 'short_supply',
  prepour_outside_window: 'outside_window',
  prepour_not_today: 'not_today',
  prepour_already_recorded: 'already_recorded',
  prepour_item_superseded: 'superseded',
  prepour_item_held: 'held',
  prepour_dose_held: 'held',
  prepour_item_not_found: 'not_found',
  medication_needs_discontinue: 'needs_discontinue',
  medication_unordered_complete_instead: 'no_order',
  medication_held_release_first: 'held',
  validation_failed: 'invalid',
  forbidden: 'forbidden',
};

async function remote(call) {
  try {
    return await call();
  } catch (e) {
    if (e instanceof ApiError) {
      throw new PrePourError(MAIN_CODES[e.code] || e.code, e.message, { status: e.status, details: e.details, portCode: e.code });
    }
    // A time the page could not read stopped the request before it was sent (strict24): said as itself, never as
    // the server being unreachable.
    if (e instanceof UnreadableTimeError) throw new PrePourError(e.code, e.message, { times: e.times });
    throw new PrePourError('unavailable', 'Could not reach the server. Try again.');
  }
}

/** Signed pours. A real patient's come from the server; the sample sheet reads its own. */
export async function fetchPours(pk, ctx) {
  if (!isRemote(pk)) return getPours(pk);
  const list = await remote(() => listPours(ctx.patient));
  return poursFromServer(poursFromPort(list));
}

/**
 * Sign a pour. `request` carries signature, palette, skipped and meds[] as the page builds it; the server reads the
 * medication's name, dose and held slots itself. Resolves to { pourid, take }; rejects with a PrePourError.
 */
export async function signPour(ctx, request) {
  const result = await remote(() => signPortPour(ctx.patient, pourRequestForPort(request)));
  return { code: 'ok', pourid: result.pour && result.pour.id, take: result.take, skipped_recorded: result.skipped_recorded };
}

/**
 * What was given from the pours: `pieceId|day|medId|time` → { outcome, witness, notes, actualTime, at, … }.
 * A real patient's come from the server and are filed against `pours`.
 */
export async function fetchAdministrations(pk, ctx, pours) {
  if (!isRemote(pk)) return getAdministrations(pk);
  const rows = await remote(() => listPourAdministrations(ctx.patient));
  return administrationsFromServer(administrationsFromPort(rows), pours);
}

/**
 * Record a time block as given: `request` is { witness, actualTime: 'HH:MM', slots: [{ itemid, outcome, notes }] }.
 * The giver is the token; the window is the server's. Rejects with a PrePourError.
 */
export function recordAdministration(ctx, request) {
  return remote(() => recordPourAdministration(ctx.patient, {
    witness_name: request.witness,
    actual_time: request.actualTime,
    slots: (request.slots || []).map((s) => ({ pour_item_id: Number(s.itemid), outcome: s.outcome, notes: s.notes || null })),
  }));
}

/**
 * Mark a course Completed: allowed on or after its end date, or any day for a non-prescription medication.
 * Earlier rejects with code 'needs_discontinue', and the page opens the discontinue.
 */
export function completeMedication(ctx, { medentryid, date, reason }) {
  return remote(() => completePortMedication(Number(medentryid), { completed_on: date, reason }));
}

/*
 * Hold, release, change and time move (#336) go through nursing's routes by the medication's id (PP-23), which the
 * port opens to medications:prepour as well as administer. A medication with no order is held, released and changed
 * at once on its own row (PP-24); a prescription's change is a change order for the prescriber to sign.
 */

/** Hold `m`: `hold` is { start, end, times: sheet hours or null for all, reason }. */
export function holdMedication(ctx, m, hold) {
  return remote(() => holdPortMedication(Number(m.id), holdBodyForPort(hold)));
}

/**
 * Release `m`'s holds from today on — the one in effect and any still queued, as keyon's release did (its holds
 * were rows from today on). The page asks before releasing one of several.
 */
export function releaseHold(ctx, m, reason = '') {
  return remote(async () => {
    const queued = (await listScheduledHolds(Number(m.id), { 'filter[status]': 'scheduled' })) || [];
    try {
      await clearMedicationHold(Number(m.id), reason || undefined);
    } catch (e) {
      // Nothing in effect, but a hold is queued: releasing it is cancelling it.
      if (!(e instanceof ApiError) || e.code !== 'medication_not_held' || !queued.length) throw e;
    }
    for (const h of queued.filter((x) => x.hold_type === 'hold')) {
      // eslint-disable-next-line no-await-in-loop
      await cancelScheduledHold(h.id);
    }
    return { code: 'ok' };
  });
}

/** Change `m`'s dose / frequency / times: `change` is { dose, freq, times, reason }. */
export function changeMedication(ctx, m, change) {
  return remote(() => {
    const body = changeBodyForPort(m, change);
    return hasNoOrder(m)
      ? replaceAdministrationSchedule(Number(m.id), body)
      : replaceMedicationOrderSchedule(Number(orderIdOf(m)), body);
  });
}

/** Move `m`'s times: `move` is { times: the sheet's hours after the move, reason }. */
export function moveTimes(ctx, m, move) {
  return remote(() => replaceAdministrationSchedule(Number(m.id), moveBodyForPort(move)));
}

/** A real patient's holds from today on: [{ medentryid, start, end, times: HH:MM or null for all, reason }]. */
export async function fetchHolds(ctx) {
  return holdsFromPort(await remote(() => listPatientHolds(ctx.patient)));
}

/** A real patient's History, oldest first. */
export async function fetchStatusEvents(ctx) {
  return eventsFromPort(await remote(() => listStatusEvents(ctx.patient)));
}

/** The next pour's template: a real patient's is the last signed pour (`pours`); the sample keeps its own. */
export function templateFor(pk, pours) {
  return isRemote(pk) ? templateFromPours(pours) : getTemplate(pk);
}

function key(kind, patientKey) {
  return `ppProto:${kind}:${patientKey}`;
}
function read(kind, patientKey, dflt) {
  try {
    const v = localStorage.getItem(key(kind, patientKey));
    return v ? JSON.parse(v) : dflt;
  } catch (e) {
    return dflt;
  }
}
function write(kind, patientKey, value) {
  try {
    localStorage.setItem(key(kind, patientKey), JSON.stringify(value));
  } catch (e) { /* quota / private mode */ }
}

// signed pours — the lock
export const getPours = (pk) => read('pours', pk, []);
// a real patient's pours change only through PourAndSign; edits made in the page are not written back
export const savePours = (pk, pours) => { if (!isRemote(pk)) write('pours', pk, pours); };

// what each pre-poured slot's administration recorded: outcome, witness, actual time, notes
export const getAdministrations = (pk) => read('admin', pk, {});
// a real patient's change only through PrePourAdminister
export const saveAdministrations = (pk, admin) => { if (!isRemote(pk)) write('admin', pk, admin); };

// tablets on hand after a pour, by medication id — the sample sheet's only: a real patient's count is
// medications.available, which PourAndSign takes from and GetPatientMeds returns
export const getOnHand = (pk) => (isRemote(pk) ? {} : read('onhand', pk, {}));
export const saveOnHand = (pk, onHand) => write('onhand', pk, onHand);

// discontinue requests, by medication id
export const getDiscontinues = (pk) => read('dc', pk, {});
export function saveDiscontinue(pk, medId, rec) {
  const all = getDiscontinues(pk);
  all[medId] = rec;
  write('dc', pk, all);
}

// status record (Hold / Change / Completed …), holds and per-time doses, by medication id - the sample sheet's
// only: a real patient's holds and changes are keyon's (fetchHolds, fetchStatusEvents)
export const getStatuses = (pk) => (isRemote(pk) ? {} : read('status', pk, {}));
export function saveStatus(pk, medId, rec) {
  if (isRemote(pk)) return;
  const all = getStatuses(pk);
  all[medId] = rec;
  write('status', pk, all);
}

// every status change, dated. A real patient's History is keyon's (fetchStatusEvents), except a discontinue's: the
// discontinue itself is still recorded in this browser until its sign-off moves to keyon, and so is its entry.
const LOCAL_CHANGE_KINDS = ['Discontinue', 'DC-pending'];
const localKinds = (pk, changes) => (isRemote(pk) ? changes.filter((c) => LOCAL_CHANGE_KINDS.includes(c.status)) : changes);
export const getChanges = (pk) => localKinds(pk, read('changes', pk, []));
export const saveChanges = (pk, changes) => write('changes', pk, localKinds(pk, changes));

// the shape of the last signed pour, reused as defaults
export const getTemplate = (pk) => read('template', pk, null);
export const saveTemplate = (pk, template) => write('template', pk, template);

/** The prototype's "Reset local data" for one patient. */
export function clearLocal(pk) {
  ['pours', 'admin', 'onhand', 'dc', 'template', 'changes'].forEach((k) => {
    try { localStorage.removeItem(key(k, pk)); } catch (e) { /* private mode */ }
  });
}
